// Import the font file URL (Vite will handle bundling)
import fontUrl from '@alchemiesolns/svg-format/src/PTSans-Bold.woff?url';

let cachedFontBase64: string | null = null;

const MIN_WOFF_BYTES = 500;
const FETCH_ATTEMPTS = 4;
const RETRY_DELAYS_MS = [0, 120, 350, 800];

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

/** Binary → base64 without FileReader (more reliable on slow or constrained devices). */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  let binary = '';
  for (let i = 0; i < bytes.length; i += chunkSize) {
    binary += String.fromCharCode(...bytes.subarray(i, Math.min(i + chunkSize, bytes.length)));
  }
  return btoa(binary);
}

/**
 * Warm the font embed cache so the first SVG export does not race a cold `fetch`
 * on slower machines (disk/network‑bound first load).
 */
export function prefetchPTSansBoldForExport(): void {
  void getPTSansBoldBase64();
}

/**
 * Loads the PTSans-Bold font and converts it to base64 for embedding in SVG.
 * The result is cached for subsequent calls after a successful load.
 */
export async function getPTSansBoldBase64(): Promise<string> {
  if (cachedFontBase64) {
    return cachedFontBase64;
  }

  let lastError: unknown;
  for (let attempt = 0; attempt < FETCH_ATTEMPTS; attempt += 1) {
    if (attempt > 0) {
      await delay(RETRY_DELAYS_MS[attempt] ?? 400);
    }
    try {
      const response = await fetch(fontUrl, { cache: 'force-cache' });
      if (!response.ok) {
        throw new Error(`Font fetch HTTP ${response.status}`);
      }
      const blob = await response.blob();
      if (!blob || blob.size < MIN_WOFF_BYTES) {
        throw new Error(`Font blob too small: ${blob?.size ?? 0}`);
      }
      const buf = await blob.arrayBuffer();
      const base64 = arrayBufferToBase64(buf);
      if (!base64 || base64.length < 200) {
        throw new Error('Invalid base64 from font buffer');
      }
      cachedFontBase64 = base64;
      return base64;
    } catch (e) {
      lastError = e;
      console.warn(`[fontEmbed] PTSans-Bold load attempt ${attempt + 1}/${FETCH_ATTEMPTS} failed`, e);
    }
  }

  console.error('[fontEmbed] Failed to load font for SVG embedding after retries:', lastError);
  return '';
}

/**
 * Generates the @font-face CSS rule for embedding in SVG.
 * Returns an empty string if there is no font data (callers should omit the rule).
 */
export function generateFontFaceRule(base64Data: string): string {
  if (!base64Data) {
    return '';
  }

  return `@font-face {
  font-family: 'PTSans-Bold';
  src: url(data:font/woff;base64,${base64Data}) format('woff');
  font-weight: bold;
  font-style: normal;
}`;
}
